import { useShowDialog } from 'App'
import { observer } from 'mobx-react'
import type { MouseEvent } from 'react'
import styled from 'styled-components'
import { VehicleHealthStatus, VehicleMaintenance } from 'models/Vehicles/VehicleMaintenanceModel'
import type { ExistingVehicle } from 'models/Vehicles/VehicleModel'
import { ProfileUpdateRequestStatus } from 'models/ota/ProfileUpdateRequestModel'
import type { Instance } from 'mobx-state-tree'
import type { CenterSummaryModel } from 'models/CenterSettings/CenterModel'
import type { VehicleServiceStatus, VehicleState } from 'models/Vehicles/VehicleStateModel'
import { getLinkForVehiclesPageWithSelectedVehicle } from 'router'
import { isOversightControl } from 'stores/rootStore'
import { svgNoShrinkClassName } from 'styles/globalCss'
import { theme } from 'styles/theme'
import { SecondaryButton } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import { SvgIcons } from '../Icon/Icon'
import { RowActionButton } from '../RowActionButton/RowActionButton'
import { showTransferVehicleDialog } from './TransferVehiclePopup'
import { showChangeStatusDialog } from './ChangeStatusPopup'
import { VehicleServiceStatusBadge } from './VehicleServiceStatusBadge'
import { showProfileUpdateDialog } from './ProfileUpdatePopup'
import { getInstalledProfileTitle } from './vehiclesSearch'
import type { ProfilesStoreType, ProfileUpdateRequestsStoreType } from 'stores/RootStoreModel'
import { Row, ScrollableTable, SortDirection, Tbody, Td, Th, Thead, ThSortable, Tr, TrWithLink } from '../Table/Table'
import { VehiclesSort, VehiclesSortKey } from './vehiclesSort'

// Holds an icon's tooltip, since a title given to an svg icon is not shown by the browser
const IconWithTooltip = styled.span`
    display: flex;
`

interface VehicleHealthIndicatorProps {
    vehicleMaintenanceInfo: VehicleMaintenance | undefined
}
const VehicleHealthIndicator = observer(({ vehicleMaintenanceInfo } : VehicleHealthIndicatorProps) => {
    if (vehicleMaintenanceInfo === undefined) {
        return null
    }
    // Discrepancy reports have their own indicator, so they don't make the vehicle's health a warning
    switch (vehicleMaintenanceInfo.partsHealthStatus){
        case VehicleHealthStatus.HEALTHY:
            return null
        case VehicleHealthStatus.UNHEALTHY:
            return <IconWithTooltip {...{ title: vehicleMaintenanceInfo.getHealthMessage }}>
                <SvgIcons.VehicleErrorNewSvg {...{ className: svgNoShrinkClassName }}/>
            </IconWithTooltip>
        case VehicleHealthStatus.WARNING:
            return <IconWithTooltip {...{ title: vehicleMaintenanceInfo.getHealthMessage }}>
                <SvgIcons.VehicleWarningNewSvg {...{ className: svgNoShrinkClassName }}/>
            </IconWithTooltip>
    }
})

interface DiscrepancyReportsIndicatorProps {
    vehicleMaintenanceInfo: VehicleMaintenance | undefined
}
// Shown when the vehicle has unresolved discrepancy reports
const DiscrepancyReportsIndicator = observer(({ vehicleMaintenanceInfo }: DiscrepancyReportsIndicatorProps) => {
    const unresolvedCount = vehicleMaintenanceInfo?.unresolved_discrepancy_reports_num ?? 0
    if (unresolvedCount === 0) {
        return null
    }
    return <IconWithTooltip {...{ title: `Vehicle has ${unresolvedCount} unresolved discrepancy reports` }}>
        <SvgIcons.DiscrepancyReportNewSvg {...{ className: svgNoShrinkClassName }}/>
    </IconWithTooltip>
})

const HealthIndicators = styled(Row)`
    align-items: center;
    gap: 1.25em;
`

const RowActions = styled(Row)`
    justify-content: flex-end;
    align-items: center;
    gap: 1.75em;
`

// Unknown profile, or a profile update in progress
const ProfileStatusText = styled.span`
    font-style: italic;
`

interface VehicleProfileProps {
    vehicle: ExistingVehicle
    profilesStore: ProfilesStoreType
    profileUpdateRequestsStore: ProfileUpdateRequestsStoreType
}

// The profile column: the profile the vehicle is being updated to, or else its installed profile
const VehicleProfile = observer(({ vehicle, profilesStore, profileUpdateRequestsStore }: VehicleProfileProps) => {
    const activeRequest = profileUpdateRequestsStore.getActiveRequest(vehicle.id)
    if (activeRequest !== undefined) {
        const profileTitle = profilesStore.getProfile(activeRequest.profile_id)?.title ?? activeRequest.profile_id
        return (
            <ProfileStatusText>
                { activeRequest.status === ProfileUpdateRequestStatus.Requested ? `Pending update to ${profileTitle}` : `Updating to ${profileTitle}` }
            </ProfileStatusText>
        )
    }
    return <>{ getInstalledProfileTitle(vehicle, profilesStore) || <ProfileStatusText>Unknown</ProfileStatusText> }</>
})

// Row actions are shown only while hovering the row, or when a button is keyboard-focused (:focus-visible, so a
// mouse click doesn't leave them shown on the clicked row).
// They fade in on hover, but disappear at once when the hover ends: the transition is set only on the shown state.
const VehicleTr = styled(TrWithLink)`
    ${RowActions} {
        visibility: hidden;
        opacity: 0;
        transition: none;
    }
    :hover ${RowActions},
    :has(:focus-visible) ${RowActions} {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.1s ease-out;
    }
`

interface VehicleRowActionsProps {
    vehicle: ExistingVehicle
    vehicleState?: VehicleState
    centers: Array<Instance<typeof CenterSummaryModel>> | null
    assignToDeployment: (vehicleId: number, centerId: number) => Promise<boolean>
    profilesStore: ProfilesStoreType
    profileUpdateRequestsStore: ProfileUpdateRequestsStoreType
    // Called after requesting a profile update, to refresh the vehicles' profiles
    onProfileUpdateRequested: VoidFunction
    // Hides the profile update buttons (of the row, and of the checked vehicles)
    canRequestProfileUpdate: boolean
    // Returns true when all the vehicles were set
    setVehiclesServiceStatus: (vehicleIds: ReadonlyArray<number>, serviceStatus: VehicleServiceStatus) => Promise<boolean>
}
const VehicleRowActions = ({ vehicle, vehicleState, centers, assignToDeployment, profilesStore, profileUpdateRequestsStore, onProfileUpdateRequested, canRequestProfileUpdate, setVehiclesServiceStatus }: VehicleRowActionsProps) => {
    const showDialog = useShowDialog()
    // Stop the click from reaching the row, which would open the vehicle
    const onActionClick = (action: (vehicle: ExistingVehicle) => void) => (e: MouseEvent) => {
        e.stopPropagation()
        action(vehicle)
    }
    const showTransferDialog = () => {
        if (centers) {
            showTransferVehicleDialog({
                showDialog,
                vehicles: [vehicle],
                centers,
                assignToDeployment,
            })
        }
    }
    const showProfileUpdatePopup = () => {
        showProfileUpdateDialog({
            showDialog,
            vehicles: [vehicle],
            profilesStore,
            profileUpdateRequestsStore,
            onRequested: onProfileUpdateRequested,
        })
    }
    const showChangeStatusPopup = () => {
        showChangeStatusDialog({ showDialog, vehicleIds: [vehicle.id], currentServiceStatuses: [vehicleState?.serviceStatus], setVehiclesServiceStatus })
    }
    return (
        <RowActions>
            <RowActionButton {...{ title: 'Change status', onClick: onActionClick(showChangeStatusPopup) }}>
                <SvgIcons.EditSvg />
            </RowActionButton>
            { isOversightControl() && // Transferring a vehicle to another center is possible only in oversight control, as in the vehicle form
                <RowActionButton {...{ title: 'Transfer vehicle', onClick: onActionClick(showTransferDialog) }}>
                    <SvgIcons.SwitchHorizontalSvg />
                </RowActionButton>
            }
            { canRequestProfileUpdate &&
                <RowActionButton {...{ title: 'Profile version update', onClick: onActionClick(showProfileUpdatePopup) }}>
                    <SvgIcons.UploadCircleSvg />
                </RowActionButton>
            }
        </RowActions>
    )
}

interface VehicleRowProps extends Omit<VehicleRowActionsProps, 'vehicle'> {
    vehicle: ExistingVehicle
    vehicleMaintenanceInfo?: VehicleMaintenance
    isChecked: boolean
    setVehicleChecked: (vehicleId: number, isChecked: boolean) => void
}
const VehicleRow = observer(({ vehicle, vehicleState, vehicleMaintenanceInfo, isChecked, setVehicleChecked, ...rowActionsProps }: VehicleRowProps) =>
    <VehicleTr {...{ isSelectable: true, isSelected: isChecked, route: getLinkForVehiclesPageWithSelectedVehicle(vehicle.id) }}>
        {/* Stop the click from reaching the row, which would open the vehicle */}
        <Td {...{ onClick: (e) => { e.stopPropagation() } }}>
            <Checkbox {...{
                name: `vehicle-${vehicle.id}-checked`,
                title: 'Select vehicle',
                isChecked,
                onChange: () => { setVehicleChecked(vehicle.id, !isChecked) },
            }} />
        </Td>
        <Td>{ vehicle.id }</Td>
        <Td><VehicleProfile {...{ vehicle, profilesStore: rowActionsProps.profilesStore, profileUpdateRequestsStore: rowActionsProps.profileUpdateRequestsStore }} /></Td>
        <Td><VehicleServiceStatusBadge {...{ serviceStatus: vehicleState?.serviceStatus }} /></Td>
        <Td>
            <HealthIndicators>
                <VehicleHealthIndicator {...{ vehicleMaintenanceInfo }} />
                <DiscrepancyReportsIndicator {...{ vehicleMaintenanceInfo }} />
            </HealthIndicators>
        </Td>
        <Td><VehicleRowActions {...{ vehicle, vehicleState, ...rowActionsProps }} /></Td>
    </VehicleTr>
)

// Set on the columns themselves (not on the header cells), so they stay the same when the selection bar
// replaces the column titles
const VehiclesTableColumns = () =>
    <colgroup>
        <col style={{ inlineSize: '5em' }} />
        <col span={2} />
        {/* Status is narrow, so the health icons are close to it. Health takes the rest of the width. */}
        <col style={{ inlineSize: '12em' }} />
        <col />
        <col style={{ inlineSize: '12em' }} />
    </colgroup>
const COLUMNS_COUNT = 6

// Replaces the column titles while vehicles are checked
const SelectionBarTh = styled(Th)`
    background-color: ${theme.colors.Gray9};
    color: ${theme.colors.White};
`

const SelectionBar = styled(Row)`
    align-items: center;
    gap: 1em;
`

const SelectionCount = styled.span`
    font-weight: ${theme.fontWeights.bold};
`

const SelectionBarActions = styled(Row)`
    margin-inline-start: auto;
    gap: 0.75em;
`

// An outlined button on the dark selection bar
const SelectionBarButton = styled(SecondaryButton)`
    color: ${theme.colors.White};
    border-color: ${theme.colors.White};
`

interface SelectAllCheckboxProps {
    checkedCount: number
    shownCount: number
    onChange: VoidFunction
}
const SelectAllCheckbox = ({ checkedCount, shownCount, onChange }: SelectAllCheckboxProps) =>
    <Checkbox {...{
        name: 'all-vehicles-checked',
        title: checkedCount > 0 ? 'Clear selection' : 'Select all vehicles',
        isChecked: shownCount > 0 && checkedCount === shownCount,
        isIndeterminate: checkedCount > 0 && checkedCount < shownCount,
        onChange,
    }} />

interface VehiclesTableProps extends Omit<VehicleRowActionsProps, 'vehicle' | 'vehicleState'> {
    // The vehicles to show, after the search
    vehicles: ReadonlyArray<ExistingVehicle>
    vehicleStates: Map<string, VehicleState>
    vehicleMaintenance: Map<string, VehicleMaintenance>
    // null when the vehicles are not sorted
    sort: VehiclesSort | null
    onSort: (sortKey: VehiclesSortKey) => void
    // Only shown vehicles can be checked
    checkedVehicleIds: ReadonlyArray<number>
    setVehicleChecked: (vehicleId: number, isChecked: boolean) => void
    setCheckedVehicleIds: (vehicleIds: ReadonlyArray<number>) => void
}

export const VehiclesTable = observer(({
    vehicles,
    vehicleMaintenance,
    vehicleStates,
    sort,
    onSort,
    checkedVehicleIds,
    setVehicleChecked,
    setCheckedVehicleIds,
    ...rowActionsProps
}: VehiclesTableProps) => {
    const showDialog = useShowDialog()
    const checkedVehicles = vehicles.filter(vehicle => checkedVehicleIds.includes(vehicle.id))

    // Checks all the shown vehicles, or clears the selection when some are already checked
    const toggleAllChecked = () => {
        setCheckedVehicleIds(checkedVehicles.length > 0 ? [] : vehicles.map(vehicle => vehicle.id))
    }
    const showProfileUpdatePopupForChecked = () => {
        showProfileUpdateDialog({
            showDialog,
            vehicles: checkedVehicles,
            profilesStore: rowActionsProps.profilesStore,
            profileUpdateRequestsStore: rowActionsProps.profileUpdateRequestsStore,
            onRequested: (isAllRequested) => {
                if (isAllRequested) {
                    setCheckedVehicleIds([])
                }
                rowActionsProps.onProfileUpdateRequested()
            },
        })
    }
    const showChangeStatusPopupForChecked = () => {
        showChangeStatusDialog({
            showDialog,
            vehicleIds: checkedVehicles.map(vehicle => vehicle.id),
            currentServiceStatuses: checkedVehicles.map(vehicle => vehicleStates.get(vehicle.id.toString())?.serviceStatus),
            setVehiclesServiceStatus: rowActionsProps.setVehiclesServiceStatus,
            onApplied: (isAllSet) => {
                if (isAllSet) {
                    setCheckedVehicleIds([])
                }
            },
        })
    }
    const showTransferPopupForChecked = () => {
        const { centers } = rowActionsProps
        if (centers) {
            showTransferVehicleDialog({
                showDialog,
                vehicles: checkedVehicles,
                centers,
                assignToDeployment: rowActionsProps.assignToDeployment,
                onTransferred: (isAllTransferred) => {
                    if (isAllTransferred) {
                        setCheckedVehicleIds([])
                    }
                },
            })
        }
    }
    const selectAllCheckbox = <SelectAllCheckbox {...{
        checkedCount: checkedVehicles.length,
        shownCount: vehicles.length,
        onChange: toggleAllChecked,
    }} />

    return (
        <ScrollableTable>
            <VehiclesTableColumns />
            <Thead>
                { checkedVehicles.length > 0 ?
                    <Tr>
                        <SelectionBarTh {...{ colSpan: COLUMNS_COUNT }}>
                            <SelectionBar>
                                { selectAllCheckbox }
                                <SelectionCount>{ checkedVehicles.length } selected</SelectionCount>
                                <SelectionBarActions>
                                    <SelectionBarButton {...{
                                        text: 'Edit vehicle status',
                                        buttonStyle: 'thin',
                                        onClick: showChangeStatusPopupForChecked,
                                    }} />
                                    { isOversightControl() && // As in the row actions
                                        <SelectionBarButton {...{
                                            text: 'Transfer vehicles',
                                            buttonStyle: 'thin',
                                            onClick: showTransferPopupForChecked,
                                        }} />
                                    }
                                    { rowActionsProps.canRequestProfileUpdate &&
                                        <SelectionBarButton {...{
                                            text: 'Check for profile update',
                                            buttonStyle: 'thin',
                                            onClick: showProfileUpdatePopupForChecked,
                                        }} />
                                    }
                                </SelectionBarActions>
                            </SelectionBar>
                        </SelectionBarTh>
                    </Tr> :
                    <Tr>
                        <Th>{ selectAllCheckbox }</Th>
                        { [
                            { sortKey: VehiclesSortKey.Number, title: 'Vehicle Number' },
                            { sortKey: VehiclesSortKey.Profile, title: 'Profile' },
                            { sortKey: VehiclesSortKey.Status, title: 'Status' },
                        ].map(({ sortKey, title }) =>
                            <ThSortable {...{
                                key: sortKey,
                                sortKey,
                                sortedBy: sort?.sortKey ?? '',
                                sortDirection: sort?.sortDirection ?? SortDirection.Asc,
                                onClick: () => { onSort(sortKey) },
                            }}>{ title }</ThSortable>
                        ) }
                        <Th />
                        <Th />
                    </Tr>
                }
            </Thead>
            <Tbody>
                { vehicles.map(vehicle => <VehicleRow {...{
                    key: vehicle.id,
                    vehicle,
                    vehicleState: vehicleStates.get(vehicle.id.toString()),
                    vehicleMaintenanceInfo: vehicleMaintenance.get(vehicle.id.toString()),
                    isChecked: checkedVehicleIds.includes(vehicle.id),
                    setVehicleChecked,
                    ...rowActionsProps,
                }} />) }
            </Tbody>
        </ScrollableTable>
    )
})
